/**
* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import {
  Entity,
  EntityLink,
  getCompoundEntityRef,
} from '@backstage/catalog-model';
import {
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';

import { ItemCardHeader } from '../../common/ItemCardHeader';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Tags } from '../../common/Tags';
import { LinkButton } from '@backstage/core-components';


const useStyles = makeStyles(theme => ({
  cardHeader: {
    position: 'relative',
  },
  title: {
    backgroundImage: 'unset',
    color: theme.palette.text.primary,
    flexGrow: 1,
    alignSelf: 'stretch',
  },
  box: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 10,
    '-webkit-box-orient': 'vertical',
    maxHeight: '40px',
  },
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '0.65rem',
    fontWeight: 'bold',
    letterSpacing: 0.5,
    lineHeight: 1,
    paddingBottom: '0.2rem',
  },
  linksLabel: {
    padding: '0 16px',
  },
  description: {
    margin: '0px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    lineClamp: 3,
    boxOrient: 'vertical',
  },
  leftButton: {
    marginRight: 'auto',
  },
  starButton: {
    padding: theme.spacing(0.25),
    color: theme.palette.text.primary,
  },
  linkIconButton: {
    margin: theme.spacing(0.5),
    SVG: {
      height: '12px',
      width: '12px',
    },
  },
}));

export type CatalogCardProps = {
  entity: Entity;
};

type CatalogEntityProps = {
  description: string;
  tags: string[];
  title: string;
  type: string;
  name: string;
  links: EntityLink[];
  lifecycle: string;
  release: string;
  version: string;
  owner : string, 
  chartAppVersion : string
};

const getCatalogEntityProps = (
  entity: Entity,
): CatalogEntityProps & { key: string } => {
  return {
    key: entity.metadata.uid!,
    name: entity.metadata.name,
    title: `${entity.metadata.title || entity.metadata.name}`,
    type: `${entity.spec?.type || ''}`,
    description: entity.metadata.description ?? '-',
    tags: (entity.metadata?.tags as string[]) ?? [],
    links: entity.metadata.links ?? [],
    lifecycle: (entity.spec?.lifecycle as string) ?? '-',
    release: (entity.metadata?.currentRelease as string) ?? '-',
    version: (entity.spec?.version as string) ?? '-',
    owner: (entity.spec?.owner as string) ?? '-',
    chartAppVersion : (entity.spec?.chartAppVersion as string) ?? '-'
  };
};

export const CatalogCard = ({ entity }: CatalogCardProps) => {
  const templateProps = getCatalogEntityProps(entity);
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);

  return (
    <Card
      style={{
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Box
        style={{
          flexDirection: 'row',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
        }}
      >
        <ItemCardHeader
          title={templateProps.title}
          classes={{ root: classes.title }}
          componentType={templateProps.type}
        />
        {/* <Box>
          <FavoriteEntity className={classes.starButton} entity={entity} />
        </Box> */}
      </Box>
      <Divider />
      <CardContent
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        {/* Removed - External User*/}
        {/* <Box className={classes.box}>
          <Typography variant="body2" className={classes.label}>
            Owner
          </Typography>
          <EntityRefLinks
            entityRefs={ownedByRelations}
            hideIcons
            variant="body2"
            defaultKind="Group"
          />
        </Box> */}
        <Box display="flex" flexDirection="row">
          {entity.spec?.owner !== 'bh-cordant-app-admin' && (
            <Box className={classes.box} flexBasis="100px" flexGrow={1}>
            <Typography variant="body2" className={classes.label}>
              Life Cycle
            </Typography>
            <Box display="flex" flexDirection="row" alignItems="center">
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  marginRight: '8px',
                  background: '#02A783',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0,
                  borderRadius: 50,
                }}
              />
              <Typography
                variant="body2"
                style={{ textTransform: 'capitalize' }}
              >
                {templateProps.lifecycle}
              </Typography>
            </Box>
          </Box>
          )}
          <Box className={classes.box} flexBasis="100px" flexGrow={1}>
            <Typography variant="body2" className={classes.label}>
              {templateProps.owner === 'bh-cordant-app-admin' ? 'Version' : 'Release'}
            </Typography>
            <Typography variant="body2">
  {templateProps.type === "tools"
    ? templateProps.chartAppVersion
    : (templateProps.type === "constructs" || templateProps.type === "stamps")
    ? templateProps.version
    : templateProps.release}
</Typography>
          </Box>
          <Box className={classes.box} flexBasis="100px" flexGrow={1}>
            <Typography variant="body2" className={classes.label}>
              Type
            </Typography>
            <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
              {templateProps.type}
            </Typography>
          </Box>
        </Box>
        <Tags tagArray={templateProps.tags} tagTextLimit={40} tagLimit={2} />

        <Box>
          <Typography variant="body2" className={classes.label}>
            Description
          </Typography>
          <Tooltip title={templateProps.description} placement="top">
            <Typography variant="body2" className={classes.description}>
              {templateProps.description}
            </Typography>
          </Tooltip>
        </Box>
      </CardContent>

      <Divider />
      <CardActions>
        <LinkButton
          size="small"
          color="default"
          variant="outlined"
          to={entityRoute(getCompoundEntityRef(entity)).toLocaleLowerCase(
            'en-US',
          )}
        >
          View
        </LinkButton>
      </CardActions>
    </Card>
  );
};
